/**
 * The vocabulary of the files a document carries inside itself.
 *
 * A packaged document (`xl/embeddings/*`, `word/embeddings/*`, `ppt/embeddings/*`) can hold whole
 * files as icons. Each one becomes an ordinary child document, and each icon is an *occurrence*:
 * one row per object per parent capture, whose state says what happened to it. The states and the
 * refusal reasons are enumerated here because three sides read them — the Convex schema column,
 * the Documents screen, and the worker, which names the reasons on the wire
 * (`EmbeddedObjectReason` in `apps/document-processor/app/models.py`).
 *
 * Order is the schema fingerprint on the Convex side: a member may be appended and none may leave.
 */
/**
 * What became of one occurrence.
 *
 * - `pending` — a reservation scoped to the attempt that wrote it; it owns nothing yet.
 * - `extracted` — the payload became a child artifact.
 * - `listed` — the object was named and not extracted; `listedReason` says why. The contract
 *   spells the pair `listed:<reason>`, which is what {@link formatEmbeddedObjectState} renders.
 * - `abandoned` — the attempt that reserved it finalised without activating.
 * - `deleted` — a person deleted the child.
 * - `absent` — a later complete inventory of the same parent no longer carries the object.
 */
export const EMBEDDED_OBJECT_STATES = [
    "pending",
    "extracted",
    "listed",
    "abandoned",
    "deleted",
    "absent",
];
/**
 * Why an object was listed rather than extracted — one sentence a person is shown next to the
 * object it refused, which is why the set is closed and shared with the worker verbatim.
 *
 * `too_large` is the one reason no request extends: depth and the descendant budget are
 * allowances, the upload size limit is not.
 */
export const EMBEDDED_OBJECT_LISTED_REASONS = [
    "linked",
    "unsupported",
    "cap",
    "too_large",
    "depth",
    "outside_read_scope",
    "not_requested",
    "upload_failed",
];
const LISTED_REASON_SET = new Set(EMBEDDED_OBJECT_LISTED_REASONS);
export function isEmbeddedObjectListedReason(value) {
    return typeof value === "string" && LISTED_REASON_SET.has(value);
}
/** The sentence each refusal is shown as, in the parent's inventory and the child's absence. */
const LISTED_REASON_SENTENCES = {
    linked: "linked object, bytes not in file",
    unsupported: "unsupported object type",
    cap: "past this document's embedded-file budget",
    too_large: "larger than the upload size limit",
    depth: "past the automatic extraction depth or the root's allowance",
    outside_read_scope: "outside what this reading covered",
    not_requested: "discovered by a request that asked for other objects",
    upload_failed: "the payload could not be stored",
};
export function describeEmbeddedObjectListedReason(reason) {
    return LISTED_REASON_SENTENCES[reason];
}
/**
 * The state as the contract spells it: `listed:<reason>` for a listed object, the bare state
 * otherwise. Stored as two columns — a composed string is neither indexable nor closed — and
 * rendered back into one here so the vocabulary people read has exactly one body.
 */
export function formatEmbeddedObjectState(state, listedReason) {
    return state === "listed" && listedReason ? `listed:${listedReason}` : state;
}
/** Where an object sits outside a Word document's body; an object in the body has no part. */
export const EMBEDDED_OBJECT_PARTS = ["header", "footer", "footnote", "endnote"];
const OBJECT_PART_SET = new Set(EMBEDDED_OBJECT_PARTS);
export function isEmbeddedObjectPart(value) {
    return typeof value === "string" && OBJECT_PART_SET.has(value);
}
/**
 * `p. 6`, `pp. 6–7`, `slide 7`, `slides 6–7`: the one wording of a page or slide span, which an
 * embedded object's locator and a document citation share. An end that is absent or equal to the
 * start is a single page.
 */
export function formatPageLabel(start, end, unit) {
    const span = end !== null && end !== undefined && end !== start;
    if (unit === "slide")
        return span ? `slides ${start}–${end}` : `slide ${start}`;
    return span ? `pp. ${start}–${end}` : `p. ${start}`;
}
function locatorText(value) {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
}
function locatorNumber(value) {
    return typeof value === "number" && Number.isInteger(value) && value >= 1 ? value : undefined;
}
/**
 * THE one place an occurrence's preposition and locator are chosen, from the fields alone.
 *
 * The first field present decides: a sheet (or a bare cell) is `at`, a document part `in the`, a
 * slide `on` with its title as `detail`, a heading `under` with the page appended where there is
 * one, and a page with no heading above it `on`. No field is no place at all.
 */
export function formatEmbeddedWhere(entry) {
    const sheetName = locatorText(entry.sheetName);
    const anchor = locatorText(entry.anchor);
    if (sheetName && anchor)
        return { preposition: "at", locator: `${sheetName}!${anchor}` };
    if (sheetName || anchor)
        return { preposition: "at", locator: sheetName ?? anchor ?? "" };
    if (entry.part)
        return { preposition: "in the", locator: entry.part };
    const heading = locatorText(entry.heading);
    const slide = locatorNumber(entry.slide);
    if (slide !== undefined) {
        return {
            preposition: "on",
            locator: formatPageLabel(slide, undefined, "slide"),
            ...(heading ? { detail: heading } : {}),
        };
    }
    const page = locatorNumber(entry.page);
    const pages = page === undefined ? undefined : formatPageLabel(page, locatorNumber(entry.pageEnd), "page");
    if (heading)
        return { preposition: "under", locator: pages ? `${heading}, ${pages}` : heading };
    if (pages)
        return { preposition: "on", locator: pages };
    return undefined;
}
/**
 * `Sheet2!P32`, `Sheet2`, `5.2 Source Systems Involved, p. 6`, `pp. 6–7`, `slide 7`, `header`,
 * or nothing: the locator a citation and a provenance line share, with no preposition and no
 * slide title.
 */
export function formatEmbeddedAnchor(entry) {
    return formatEmbeddedWhere(entry)?.locator;
}
/**
 * The separator a provenance line joins its parts with — a container and what sits inside it,
 * never a path. Shared with the zip-archive line (`document-provenance.ts`), which is why it
 * lives here rather than beside either caller: the two lines read as one family only if they
 * join on the same character.
 */
export const PROVENANCE_JOIN = "›";
/** `HLD_Details.xlsx › Sheet2!P32` — the zip-style provenance line a child row carries. */
export function formatEmbeddedProvenance(provenance) {
    const locator = formatEmbeddedAnchor(provenance);
    return locator ? `${provenance.parentName} ${PROVENANCE_JOIN} ${locator}` : provenance.parentName;
}
/**
 * How many occurrences one targeted extraction request may name.
 *
 * Shared because three sides hold it: the tool's schema states it to the model, the Convex
 * dispatcher enforces it (a Convex validator cannot bound an array), and the job it creates is
 * bounded by it. The worker's own `EMBEDDED_OCCURRENCE_KEYS_MAX` is wider; this is the narrower
 * of the two and therefore the one a request is graded against.
 */
export const EMBEDDED_REQUEST_MAX_OCCURRENCES = 32;
/**
 * What an extracted object's child is doing now, in the one vocabulary every surface reads.
 *
 * `deleted` and `unplaced` are facts the child's own processing cannot state: the first is a
 * child a person removed, and the second is one registered under an occurrence nothing has
 * published — it exists, and it is on no screen and in no queue. A child parked on the pre-read
 * question reads as `queued`, which is what it is from the parent's inventory: unfinished work
 * that has not failed, and a read of the child itself is what says more.
 */
export const EMBEDDED_CHILD_STATUSES = [
    "queued",
    "processing",
    "ready",
    "failed",
    "deleted",
    "unplaced",
];
/**
 * How many embedded objects one `document_outline` call names before it hands back a cursor.
 *
 * Discovery is not bounded by the extraction budget, so a workbook can carry hundreds of icons
 * and every one of them has to be addressable — but an outline is a map rather than a listing,
 * and a page of fifty is what a reader chooses from.
 */
export const DOCUMENT_OUTLINE_MAX_EMBEDDED_FILES = 50;
/**
 * What a person may ask for one listed object.
 *
 * - `extract` — it was named and never extracted, and no rule refuses it outright.
 * - `extract_again` — it had a child and a person deleted it; asking again makes a fresh one.
 * - `none` — nothing can be asked: the payload is over the upload ceiling, the parent no longer
 *   carries the object, or the child is already here.
 */
export const EMBEDDED_OBJECT_OFFERS = ["extract", "extract_again", "none"];
/**
 * The refusals no request lifts: the same bytes read again give the same answer.
 *
 * `linked` names an object the workbook holds only a link to, `unsupported` a type this reader
 * cannot open, `too_large` a payload over the upload ceiling. Every other reason is a budget,
 * a scope or a transient loss, which the request above extends or a retry repairs. This is the
 * one place that says so: the offer, the request gate and a surface's tone all read it.
 */
export const EMBEDDED_PERMANENT_REFUSALS = ["linked", "unsupported", "too_large"];
const PERMANENT_REFUSAL_SET = new Set(EMBEDDED_PERMANENT_REFUSALS);
export function isEmbeddedRefusalPermanent(reason) {
    return reason !== undefined && PERMANENT_REFUSAL_SET.has(reason);
}
/**
 * What may be asked for one occurrence, from the row alone.
 *
 * A permanent refusal is not offered, and an `absent` occurrence describes bytes the parent no
 * longer carries — offering either would spend a job on an answer that cannot change.
 */
export function embeddedObjectOffer(state, listedReason) {
    if (state === "deleted")
        return "extract_again";
    if (state === "listed")
        return isEmbeddedRefusalPermanent(listedReason) ? "none" : "extract";
    if (state === "abandoned")
        return "extract";
    return "none";
}
